'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { defaultLocale } from '../i18n';

interface QueryProviderProps {
  children: React.ReactNode;
}

function getLocaleFromPath(pathname: string | null) {
  if (!pathname) return;
  // 예) '/ko/notices' -> 'ko-KR'
  //    '/en/some-page' -> 'en-US'
  //    혹은 원하는 세그먼트 위치 사용
  const segments = pathname.split('/').filter(Boolean); // ['', 'ko-KR', 'notices'] -> ['ko-KR', 'notices']
  return segments[0] ?? defaultLocale; // 기본값 'ko-KR'
}

export default function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1분
          },
        },
      }),
  );

  const pathname = usePathname();
  const previousLocaleRef = useRef<string>('');

  useEffect(() => {
    const currentLocale = getLocaleFromPath(pathname);
    // 이전 로케일과 비교
    if (currentLocale !== previousLocaleRef.current) {
      // 로케일이 실제로 바뀐 경우에만 invalidate
      queryClient.invalidateQueries();
      previousLocaleRef.current = currentLocale || defaultLocale;
    }
  }, [pathname, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
